import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import moment from 'moment';
import { IntlState } from 'react-intl-redux';
import { Assignment } from '@mui/icons-material';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';
import { sortByString } from '../utilities/Sorting';
import ZerpaTable, { ColumnObject, ZerpaTableStateSetters } from '../layout/table/ZerpaTable';
import { downloadFromContainer } from '../../utilities/urlHelper';
import { MANUALS_CONTAINER_NAME } from '../../environment';
import TrialDetails from './TrialDetails';
import { getAccreditedStatusId } from '../utilities/TrialUtility';
import {
  RootState,
  Trial,
  TrialCategory,
  TrialCategoryValue,
  ThirdPartyTrial,
  Locale,
  TrialTagMapping,
  ZerpaState,
} from '../../redux/types';

type TrialExtended = Trial & {
  thirdPartyTrial?: ThirdPartyTrial,
  trialTagMapping?: TrialTagMapping[],
};

type CategoryMap = {
  [key: number]: TrialCategory
}

type State = {
  categoryMap?: CategoryMap
}

class TrialsList extends Component<Props, State> {
  baseTrialColumns: ColumnObject[] = [
    {
      keyName: 'nameText',
      text: <FormattedMessage id="trials.trialName" />,
      sortable: true,
    },
    {
      keyName: 'tag',
      text: <FormattedMessage id="trials.tag" />,
      sortable: false,
    },
    {
      keyName: 'endYear',
      text: <FormattedMessage id="trials.trialYear" />,
      sortable: true,
    },
    {
      keyName: 'trialStatus',
      text: <FormattedMessage id="trials.status" />,
      sortable: true,
    },
    {
      keyName: 'enroller',
      text: <FormattedMessage id="trials.enroller" />,
      sortable: true,
    },
    {
      keyName: 'isAccredited',
      text: <FormattedMessage id="trials.accreditedStatus" />,
    },
    {
      keyName: 'manual',
      text: <FormattedMessage id="trials.manual" />,
    },
  ];

  trialsColumnWithCategory: ColumnObject[] = [
    ...this.baseTrialColumns,
    {
      keyName: 'code',
      text: <FormattedMessage id="trials.trialCode" />,
      sortable: true,
    },
    {
      keyName: 'trialCategoryValue',
      text: <FormattedMessage id="trialSetup.trialCategory" />,
      sortable: true,
    },
    {
      keyName: 'international',
      text: 'international',
      sortable: true,
    },
    {
      keyName: 'availableBoth',
      text: 'availableBoth',
      sortable: true,
    },
  ];

  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  componentDidUpdate() {
    const { trialCategories } = this.props;
    const { categoryMap } = this.state;
    if (trialCategories && trialCategories.length && !categoryMap) {
      const catMap = trialCategories.reduce((map, current) => {
        const newMap = { ...map, [current.categoryId]: current };
        return newMap;
      }, {} as CategoryMap);
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ categoryMap: catMap });
    }
  }

  getTrialStatus = (
    startDate: string,
    endDate: string,
    regStartDate: string,
    regEndDate: string,
    deactivate: boolean,
  ) => {
    const { intl } = this.props;
    const now = moment();
    const trialStart = moment(startDate).local();
    const trialEnd = moment(endDate).local();
    const registrationStartDate = moment(regStartDate).local();
    const registrationEndDate = moment(regEndDate).local();

    if (deactivate) {
      return intl?.formatMessage({ id: 'trialtable.finished' });
    }
    if (now.isBetween(registrationStartDate, registrationEndDate, 'day', '[]')) {
      return intl?.formatMessage({ id: 'trialtable.registrationStatus' });
    }
    if (now.isBefore(registrationStartDate)) {
      return intl?.formatMessage({ id: 'trialtable.registrationUpcomingStatus' });
    }
    if (now.isBefore(trialStart)) {
      return intl?.formatMessage({ id: 'trialtable.upcomingStatus' });
    }
    if (now.isBetween(trialStart, trialEnd, 'day', '[]')) {
      return intl?.formatMessage({ id: 'trialtable.ongoingStatus' });
    }
    if (now.isAfter(trialEnd)) {
      return intl?.formatMessage({ id: 'trialtable.pendingStatus' });
    }
    return intl?.formatMessage({ id: 'trialtable.unknownStatus' });
  };

  isloggedIn() {
    const { users } = this.props;
    return users.token?.role;
  }

  isAdmin() {
    const { users } = this.props;
    return users.token?.role === 'admin';
  }

  renderManualButton = (manual: string) => {
    const { users } = this.props;
    const token = users.token?.id;
    return (
      manual && token
        ? (
          <Assignment
            onClick={(event) => {
              event.stopPropagation();
              downloadFromContainer(MANUALS_CONTAINER_NAME, manual);
            }}
          />
        )
        : <span />
    );
  };

  renderDetails(trial: Trial) {
    const {
      renderAdditionalRows,
      renderTrialDetails,
      renderTrialActions,
    } = this.props;
    return (
      <TrialDetails
        trial={trial}
        renderAdditionalRows={renderAdditionalRows}
        renderTrialDetails={renderTrialDetails}
        renderTrialActions={renderTrialActions}
      />
    );
  }

  renderOnSelect = (key: unknown) => {
    const { trials } = this.props;
    const matchedTrial = trials.find((e) => e.id === key);
    return matchedTrial && this.renderDetails(matchedTrial);
  };

  render() {
    const {
      trials,
      showDeactivatedTrials,
      hideEndedTrials,
      hideVoucherTrials,
      hideInternalTrials,
      renderTrialStatus,
      localize,
      intl,
      trialTagById,
      onZerpaTableStateChange,
      zerpaState,
      selectedTab,
    } = this.props;
    const {
      categoryMap,
    } = this.state;
    let normalizedTrialsList = [...trials];
    if (selectedTab) {
      normalizedTrialsList = normalizedTrialsList.filter((t) => (selectedTab === 'institute' ? !t.isDRT : t.isDRT));
    }

    if (localize && intl) {
      normalizedTrialsList = normalizedTrialsList
        .filter((t) => {
          if (t.availableBoth) {
            return true;
          }
          return intl.locale === Locale.de ? !t.international : t.international;
        });
    }

    if (!showDeactivatedTrials) {
      normalizedTrialsList = normalizedTrialsList.filter((trial) => !trial.deactivate);
    }

    if (hideEndedTrials) {
      normalizedTrialsList = normalizedTrialsList.filter((trial) => moment().isSameOrBefore(
        moment(trial.registrationEndDate).local(), 'day',
      ));
    }

    if (hideVoucherTrials) {
      normalizedTrialsList = normalizedTrialsList.filter((
        trial,
      ) => trial.trialCategory !== TrialCategoryValue.voucherTrial);
    }

    if (hideInternalTrials) {
      normalizedTrialsList = normalizedTrialsList.filter((
        trial,
      ) => trial.trialCategory !== TrialCategoryValue.internalTrial);
    }
    if (!trialTagById) {
      return null;
    }
    const visibleTrialList = normalizedTrialsList
      .map(({
        name,
        startDate,
        endDate,
        registrationStartDate,
        registrationEndDate,
        deactivate,
        trialCategory,
        manual,
        trialTagMapping,
        isAccredited,
        ...others
      }) => ({
        nameText: `${name}${others.thirdPartyTrial ? ' *' : ''}`,
        name,
        startDate,
        endDate,
        registrationStartDate,
        registrationEndDate,
        deactivate,
        trialCategory,
        endYear: moment(endDate).year(),
        trialStatus: renderTrialStatus
          ? renderTrialStatus()
          : this.getTrialStatus(
            startDate, endDate, registrationStartDate, registrationEndDate, deactivate,
          ),
        ...others,
        trialCategoryValue: this.isAdmin()
          && trialCategory && categoryMap && categoryMap[trialCategory].name,
        manual: this.isloggedIn() && this.renderManualButton(manual),
        tag: trialTagMapping?.map((tag: TrialTagMapping) => (
          <Tooltip key={tag.tagId} title={trialTagById[tag.tagId].description}>
            <Chip size="small" label={trialTagById[tag.tagId].name} key={tag.id} style={{ margin: '0.1em' }} />
          </Tooltip>
        )),
        isAccredited: isAccredited === undefined || isAccredited === null ? '-' : <FormattedMessage id={getAccreditedStatusId(isAccredited)} />,
      }));

    type VisibleTrialListType = typeof visibleTrialList[0];
    const sortedTrialList = visibleTrialList.sort((a, b) => sortByString<VisibleTrialListType>('asc', 'nameText')(a, b));
    return (
      <Paper elevation={0} className="pa2" style={{ overflow: 'auto' }}>
        <ZerpaTable
          columns={this.isAdmin() ? this.trialsColumnWithCategory : this.baseTrialColumns}
          keyIdentifier="id"
          tableRows={sortedTrialList}
          renderOnSelected={this.renderOnSelect}
          getZerpaState={onZerpaTableStateChange}
          zerpaState={zerpaState}
          showPagination
          dense
          color={selectedTab === 'individual' ? 'green' : undefined}
        />
      </Paper>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  users: state.users,
  trialCategories: state.trialCategories.categories,
  trialTagById: state.trialTag.byId,
  zerpaState: state.pageState.trialmanagement.zerpaTableState,
});

// todo: shall be removed after intl upgrade v3 or above
type InjectedIntl = {
  formatMessage: (arg0: { [key: string]: string }) => void;
}

type TrialListTypes = {
  trials: TrialExtended[];
  renderTrialStatus?: (trial?: Trial) => React.ReactNode;
  showDeactivatedTrials?: boolean;
  renderTrialActions?: (trial: Trial) => React.ReactNode;
  renderAdditionalRows?: (trial: Trial) => React.ReactNode;
  onZerpaTableStateChange?: (state: ZerpaState, setters?: ZerpaTableStateSetters) => void;
  renderTrialDetails?: boolean;
  hideEndedTrials?: boolean;
  hideVoucherTrials?: boolean;
  hideInternalTrials?: boolean;
  localize?: boolean;
  intl: IntlState & InjectedIntl;
  selectedTab?: 'institute' | 'individual';
}

const connector = connect(mapStateToProps, {});
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & TrialListTypes;

export default injectIntl(connector(TrialsList));
